// Loan Health Grade chip — the live A–F grade computed server-side from the
// eight discipline lights (LoanHealthGrade rubric; the chip can never
// disagree with the scorecard). Fixed RAG semantics: A/B success, C warning,
// D/F error; terminal states (paid-off / liquidated) render an ungraded
// muted "—". Clicking opens the per-discipline breakdown popover, which also
// carries the static origination rating (demoted from its old Rating
// column) and, when enabled, the plain-language "How is this calculated?"
// rubric.

import { useEffect, useRef, useState } from "react";
import { cn } from "@/design-system/lib/utils";
import type {
  LoanDisciplineHealthDto,
  LoanHealthGradeDto,
} from "@/api/portfolioAdmin/loanHealthApi";

const GRADE_TONE_CLASS: Record<string, string> = {
  A: "bg-status-success/15 text-status-success border-status-success/30",
  B: "bg-status-success/15 text-status-success border-status-success/30",
  C: "bg-status-warning/15 text-status-warning border-status-warning/30",
  D: "bg-status-error/15 text-status-error border-status-error/30",
  F: "bg-status-error/15 text-status-error border-status-error/30",
};

const UNGRADED_CLASS = "bg-gray-mid/15 text-gray-mid border-gray-mid/30";

const DISCIPLINE_LABEL: Record<string, string> = {
  payments: "Payments",
  insurance: "Insurance",
  ucc: "UCC",
  covenants: "Covenants",
  reporting: "Reporting",
  vendors: "Vendors",
  qa: "QA",
  defaults: "Defaults",
};

const SEVERITY_DOT: Record<string, string> = {
  ok: "bg-status-success",
  warning: "bg-status-warning",
  error: "bg-status-error",
  unavailable: "bg-gray-mid",
};

export interface GradeChipProps {
  grade: LoanHealthGradeDto | null | undefined;
  /** Per-discipline severities for the breakdown popover. */
  disciplines?: LoanDisciplineHealthDto[];
  /** Static origination credit rating (A–E) shown in the popover. */
  originationRating?: string;
  /** "sm" = list cell; "lg" = the loan-dashboard hero. */
  size?: "sm" | "lg";
  /** Hero only — adds the plain-language rubric to the popover. */
  showRubric?: boolean;
  loading?: boolean;
  "data-testid"?: string;
}

export function GradeChip({
  grade,
  disciplines,
  originationRating,
  size = "sm",
  showRubric = false,
  loading = false,
  "data-testid": testId,
}: GradeChipProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (loading) {
    return (
      <span
        className={cn(
          "inline-block rounded-pill bg-white-warm animate-pulse",
          size === "lg" ? "h-10 w-14" : "h-5 w-8",
        )}
        data-testid={testId}
        aria-hidden
      />
    );
  }

  const letter = grade?.grade ?? null;
  const toneClass = letter ? GRADE_TONE_CLASS[letter] : UNGRADED_CLASS;
  const label = letter
    ? `Health grade ${letter}`
    : grade
      ? `Not graded — ${grade.accountState ?? "terminal state"}`
      : "Health grade unavailable";

  return (
    <span ref={containerRef} className="relative inline-block" data-testid={testId}>
      <button
        type="button"
        className={cn(
          "inline-flex items-center justify-center rounded-pill border font-display font-bold",
          size === "lg"
            ? "px-space-4 py-space-1 text-h2"
            : "px-space-2 py-[1px] text-small min-w-[28px]",
          toneClass,
        )}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        data-testid={testId ? `${testId}-button` : undefined}
      >
        {letter ?? "—"}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Health grade breakdown"
          className="absolute z-30 mt-space-1 left-0 w-72 bg-white-pure border border-border-light rounded-lg shadow-lg p-space-3 text-left cursor-default"
          onClick={(e) => e.stopPropagation()}
          data-testid={testId ? `${testId}-popover` : "grade-popover"}
        >
          {letter === null && grade ? (
            <p className="text-small text-gray-mid m-0 mb-space-2">
              Not graded — {grade.accountState ?? "terminal state"}. Closed
              loans are excluded from grading.
            </p>
          ) : (
            grade && (
              <p className="text-small text-onyx m-0 mb-space-2">
                <span className="font-bold">Grade {letter}</span>
                {" · "}
                {grade.warningCount} warning{grade.warningCount === 1 ? "" : "s"},{" "}
                {grade.errorCount} error{grade.errorCount === 1 ? "" : "s"}
                {grade.appliedCap && (
                  <span className="block text-caption text-gray-mid mt-space-1">
                    Capped by account state: {grade.appliedCap}
                  </span>
                )}
              </p>
            )
          )}

          {disciplines && disciplines.length > 0 && (
            <ul className="m-0 p-0 list-none grid grid-cols-2 gap-y-space-1 gap-x-space-3 mb-space-2">
              {disciplines.map((d) => (
                <li key={d.discipline} className="flex items-center gap-space-1">
                  <span
                    className={cn(
                      "h-2 w-2 rounded-pill shrink-0",
                      SEVERITY_DOT[d.severity] ?? "bg-gray-mid",
                    )}
                    aria-hidden
                  />
                  <span className="text-caption text-onyx">
                    {DISCIPLINE_LABEL[d.discipline] ?? d.discipline}
                  </span>
                  <span className="text-caption text-gray-mid ml-auto">
                    {d.severity === "unavailable" ? "n/a" : d.severity}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {originationRating && (
            <p
              className="text-caption text-gray-mid m-0 border-t border-border-light pt-space-2"
              data-testid="grade-origination-rating"
            >
              Origination rating: {originationRating}
            </p>
          )}

          {showRubric && <RubricNote />}
        </div>
      )}
    </span>
  );
}

function RubricNote() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border-light pt-space-2 mt-space-2">
      <button
        type="button"
        className="text-caption text-gold-shadow hover:underline"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="grade-rubric-toggle"
      >
        How is this calculated?
      </button>
      {open && (
        <div className="text-caption text-gray-mid mt-space-1" data-testid="grade-rubric">
          <p className="m-0 mb-space-1">
            The grade counts the warning and error lights on the health
            scorecard. Start at A: 1–2 warnings → B, 3 or more → C, any
            error → D, two or more errors or an open default case → F.
          </p>
          <p className="m-0 mb-space-1">
            Missing data is excluded, not penalized — a light that can't be
            read never lowers the grade.
          </p>
          <p className="m-0">
            The account state caps the grade: sub-performing loans cap at C,
            non-performing at D, and defaulted or charged-off loans are
            always F. Paid-off and liquidated loans are not graded.
          </p>
        </div>
      )}
    </div>
  );
}
